import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from "reactstrap";
import { RiErrorWarningLine } from "react-icons/ri";
import { withNamespaces } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import ComplianceScreeningQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/screening/QueriesService";
import ComplianceMonitoringQueriesService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/QueriesService";
import { toast } from "react-toastify";
import QueryContext from "../../contexts/QueryContext";
import { useContext } from "react";
import { useLocation } from "react-router-dom";

const CancelConfirmationModal = ({ 
    t, 
    isOpen, 
    toggle 
}) => {
    const location = useLocation();

    const redirectTo = location.state?.from || '/admin/3rd-party/manager';

    const {
        queryType,
        queryData
    } = useContext(QueryContext);

    const history = useHistory();

    const isEdit = !!queryData?.id;

    const service = queryType === 'screening' ? 
        ComplianceScreeningQueriesService.getInstance() : 
        ComplianceMonitoringQueriesService.getInstance();

    const handleDeleteQueryMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.deleteQuery(payload.id);
        },
        onSuccess: () => {
            toast(t("Query deleted successfully."), {
                type: "success"
            });
            
            history.push(redirectTo);
        },
        onError: () => {
            toast(t("Unable to delete the query. Please try again."), {
                type: "error"
            });
        }
    });

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
                <div className="d-flex justify-content-start align-items-center">
                    <RiErrorWarningLine className="me-2 text-warning" size={22} />
                    {t('Exit Query Setup')}
                </div>
            </ModalHeader>
            <ModalBody>
                {isEdit ? (
                    <>
                        <p className="mb-2 fw-semibold">
                            {t('This query has been saved as a draft. What would you like to do?')}
                        </p>
                        <small className="text-muted">
                            {t('You can resume this query later from your drafts.')}
                        </small>
                    </>
                ) : (
                    <p className="mb-0 fw-semibold">
                        {t('Are you sure you want to exit? Your progress will be lost.')}
                    </p>
                )}
            </ModalBody>

            <ModalFooter className="d-flex justify-content-between">
                {/* Continue Editing */}
                <Button color="secondary" outline onClick={toggle}>
                    {t(isEdit ? 'Continue Editing' : 'No, Keep Creating')}
                </Button>

                {/* Actions */}
                <div className="d-flex gap-2">
                    {isEdit ? (
                        <>
                            <Button
                                color="danger"
                                outline
                                onClick={() => {
                                    handleDeleteQueryMutation.mutate({ id: queryData?.id });
                                }}
                                disabled={handleDeleteQueryMutation.isLoading}
                            >
                                {handleDeleteQueryMutation.isLoading && (
                                    <Spinner size="sm" color="danger" className="me-1" />
                                )}

                                {handleDeleteQueryMutation.isLoading ? "Deleting..." : "Delete and Exit"}
                            </Button>

                            <Button 
                                color="primary" 
                                onClick={() => history.push(redirectTo)}
                            >
                                {t('Save and Exit')}
                            </Button>
                        </>
                    ) : (
                        <Button 
                            color="danger" 
                            onClick={() => history.push(redirectTo)}
                        >
                            {t('Yes, Exit')}
                        </Button>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(
    CancelConfirmationModal
);
